import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import * as api from "../api";
import Button from "../components/Button";
import Pill from "../components/Pill";
import {
  PRESETS,
  buildCostCsv,
  count,
  money,
  periodParams,
  unpricedModels,
  validateCustomRange,
  type Period,
  type PresetKey,
} from "../cost";
import type { PageProps } from "./types";

// --- shared style objects (mirror Orgs.tsx conventions) ----------------------

const th: React.CSSProperties = {
  textAlign: "left",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
  fontWeight: 400,
  padding: "8px 10px",
  borderBottom: "1px solid var(--border-hairline)",
};
const thR: React.CSSProperties = { ...th, textAlign: "right" };
const td: React.CSSProperties = { padding: "11px 10px", borderBottom: "1px solid var(--border-hairline)" };
const tdR: React.CSSProperties = { ...td, textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--ink-700)" };
const h3: React.CSSProperties = { margin: "28px 0 12px", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)" };
const dateInput: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--ink-900)", background: "#fff", border: "1px solid var(--border-strong)", borderRadius: 8, padding: "7px 10px", outline: "none" };

function Tile({ label, children, note }: { label: string; children: React.ReactNode; note?: string }) {
  return (
    <div style={{ background: "var(--surface-sunken)", border: "1px solid var(--border-hairline)", borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-muted)" }}>{label}</div>
      <div style={{ marginTop: 8, fontFamily: "var(--font-mono)", fontSize: 20, color: "var(--ink-900)", fontWeight: 500 }}>{children}</div>
      {note && <div style={{ marginTop: 4, fontSize: 11, color: "var(--text-faint)" }}>{note}</div>}
    </div>
  );
}

// --- period bar --------------------------------------------------------------

function PeriodBar({
  period,
  onApply,
  onExport,
  canExport,
  generatedAt,
}: {
  period: Period;
  onApply: (p: Period) => void;
  onExport: () => void;
  canExport: boolean;
  generatedAt: string | null;
}) {
  const [start, setStart] = useState(period.kind === "custom" ? period.start : "");
  const [end, setEnd] = useState(period.kind === "custom" ? period.end : "");
  const [rangeError, setRangeError] = useState<string | null>(null);
  const activePreset = period.kind === "preset" ? period.preset : null;

  const applyCustom = () => {
    const err = validateCustomRange(start, end);
    setRangeError(err);
    if (!err) onApply({ kind: "custom", start, end });
  };

  const presetBtn = (key: PresetKey, label: string) => (
    <button
      key={key}
      type="button"
      onClick={() => { setRangeError(null); onApply({ kind: "preset", preset: key }); }}
      style={{
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        fontWeight: activePreset === key ? 700 : 500,
        color: activePreset === key ? "#fff" : "var(--ink-700)",
        background: activePreset === key ? "var(--violet-500)" : "#fff",
        border: `1px solid ${activePreset === key ? "var(--violet-500)" : "var(--border-strong)"}`,
        borderRadius: 8,
        padding: "7px 13px",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 6 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        {PRESETS.map((p) => presetBtn(p.key, p.label))}
        <span style={{ width: 1, height: 22, background: "var(--border-default)", margin: "0 2px" }} />
        <input type="date" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} style={dateInput} aria-label="Start date" />
        <span style={{ color: "var(--text-muted)", fontSize: 13 }}>→</span>
        <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} style={dateInput} aria-label="End date" />
        <Button variant="ghost" size="sm" onClick={applyCustom}>Apply range</Button>
      </div>
      <div style={{ flex: 1 }} />
      <Button variant="secondary" size="sm" onClick={onExport} disabled={!canExport}>Export CSV</Button>
      {rangeError && <div style={{ flexBasis: "100%", color: "var(--danger-500)", fontSize: 12.5 }}>{rangeError}</div>}
      <div style={{ flexBasis: "100%", fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.04em", color: "var(--text-faint)" }}>
        Modeled estimate — internal usage × the rate card, not billed actuals (source: modeled)
        {generatedAt ? ` · as of ${new Date(generatedAt).toLocaleString()}` : ""}
      </div>
    </div>
  );
}

// --- trust banner (unpriced / STT / fixed-infra provenance, FR-008) ----------

function TrustBanner({ summary }: { summary: api.CostSummary }) {
  const unpriced = summary.unpriced;
  if (!unpriced.length) return null;
  // Name the offending model(s), not just the categories, so a deploy that rolls
  // to an unpriced model (e.g. a new strategic deployment) is obvious (#806).
  const models = unpricedModels(unpriced);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, padding: "11px 15px", background: "#FBEEDD", border: "1px solid #F0D9B5", borderRadius: 10, color: "#8A5209", fontSize: 13 }}>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none" }}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4M12 17h.01" /></svg>
      <span>
        <strong>Unpriced usage</strong> — <strong>{models.join(", ")}</strong> {models.length === 1 ? "has" : "have"} no rate on the card and {models.length === 1 ? "is" : "are"} <strong>not valued</strong>. The totals below understate cost until {models.length === 1 ? "it is" : "they are"} priced.
      </span>
    </div>
  );
}

// --- summary + unit economics ------------------------------------------------

function SummarySection({ summary, loading, error }: { summary: api.CostSummary | null; loading: boolean; error: string | null }) {
  if (error) return <p style={{ color: "var(--danger-500)", fontSize: 13.5, marginTop: 16 }}>{error}</p>;
  if (loading || !summary) return <p style={{ color: "var(--text-muted)", fontSize: 13.5, marginTop: 16 }}>Loading cost summary…</p>;
  return (
    <>
      <TrustBanner summary={summary} />
      <h3 style={h3}>Modeled variable cost</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14 }}>
        <Tile label="Total variable">{money(summary.total_variable_usd)}</Tile>
        <Tile label="LLM">{money(summary.llm_usd)}</Tile>
        <Tile label="Telephony">{money(summary.telephony_usd)}</Tile>
        <Tile label="STT" note="approximate — audio seconds proxied by call duration">{money(summary.stt_usd)}</Tile>
        <Tile
          label="Fixed infra"
          note={summary.fixed_infra.usd != null && summary.fixed_infra.monthly_usd != null
            ? `prorated from ${money(summary.fixed_infra.monthly_usd)}/mo`
            : undefined}
        >
          {summary.fixed_infra.status === "not_integrated" || summary.fixed_infra.usd == null
            ? <Pill tone="neutral">not integrated</Pill>
            : money(summary.fixed_infra.usd)}
        </Tile>
      </div>

      <h3 style={h3}>Unit economics</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14 }}>
        <Tile label="Cost / call">{money(summary.cost_per_call)}</Tile>
        <Tile label="Cost / telephony-min">{money(summary.cost_per_minute)}</Tile>
        <Tile label="Cost / active org">{money(summary.cost_per_active_org)}</Tile>
        <Tile label="Calls">{count(summary.calls)}</Tile>
        <Tile label="Active orgs">{count(summary.active_orgs)}</Tile>
      </div>
    </>
  );
}

// --- per-org table -----------------------------------------------------------

function OrgTable({ byOrg, loading, error }: { byOrg: api.CostByOrg | null; loading: boolean; error: string | null }) {
  return (
    <>
      <h3 style={h3}>Per-org cost &amp; margin <span style={{ textTransform: "none", letterSpacing: 0, color: "var(--text-faint)" }}>· highest cost first</span></h3>
      {error ? (
        <p style={{ color: "var(--danger-500)", fontSize: 13.5 }}>{error}</p>
      ) : loading || !byOrg ? (
        <p style={{ color: "var(--text-muted)", fontSize: 13.5 }}>Loading per-org costs…</p>
      ) : byOrg.orgs.length === 0 ? (
        <p style={{ color: "var(--text-muted)", fontSize: 13.5, padding: "8px 0" }}>No org had usage in this period.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={th}>Org</th><th style={th}>Tier</th>
            <th style={thR}>LLM</th><th style={thR}>Telephony</th><th style={thR}>STT</th>
            <th style={thR}>Total</th><th style={thR}>Revenue</th><th style={thR}>Margin</th><th style={thR}>Calls</th>
          </tr></thead>
          <tbody>
            {byOrg.orgs.map((o) => (
              <tr key={o.org_id}>
                <td style={td}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink-900)" }}>{o.org_name || o.org_id}</div>
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{o.org_id}</div>
                </td>
                <td style={td}><Pill tone="neutral">{o.plan_tier || "—"}</Pill></td>
                <td style={tdR}>{money(o.llm_usd)}</td>
                <td style={tdR}>{money(o.telephony_usd)}</td>
                <td style={tdR}>{money(o.stt_usd)}</td>
                <td style={{ ...tdR, color: "var(--ink-900)", fontWeight: 600 }}>{money(o.total_variable_usd)}</td>
                <td style={{ ...tdR, color: o.revenue_known ? "var(--ink-700)" : "var(--text-faint)" }}>{o.revenue_known ? money(o.revenue_usd) : "n/a"}</td>
                <td style={{ ...tdR, color: !o.revenue_known ? "var(--text-faint)" : (o.margin_usd ?? 0) < 0 ? "var(--danger-500)" : "var(--success-500)" }}>{o.revenue_known ? money(o.margin_usd) : "n/a"}</td>
                <td style={tdR}>{count(o.calls)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

// --- rate card ---------------------------------------------------------------

function RateCardPanel({
  rates,
  loading,
  error,
  isSuper,
  onSaved,
  reload,
  showToast,
}: {
  rates: api.RateRow[] | null;
  loading: boolean;
  error: string | null;
  isSuper: boolean;
  onSaved: (updated: api.RateRow) => void;
  reload: () => void;
  showToast: (m: string) => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Delete is a two-click arm (no browser modal): first click arms, second confirms.
  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const startEdit = (r: api.RateRow) => { setEditingId(r.id); setDraft(r.unit_cost_usd); setSaveError(null); };
  const cancel = () => { setEditingId(null); setSaveError(null); };
  // Mirror the backend guard (numeric, >= 0) so an invalid draft never round-trips.
  const draftValid = draft.trim() !== "" && Number.isFinite(Number(draft.trim())) && Number(draft.trim()) >= 0;

  const save = async (r: api.RateRow) => {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await api.updateRate(r.id, { unit_cost_usd: draft.trim() });
      setEditingId(null);
      onSaved(updated); // parent re-fetches summary + by-org
      showToast(`Rate updated — ${r.category}${r.model_key ? ` · ${r.model_key}` : ""} → $${updated.unit_cost_usd}/${r.unit}.`);
    } catch (e: any) {
      setSaveError(e?.message || "Save failed — rates unchanged.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: api.RateRow) => {
    setBusyId(r.id);
    setSaveError(null);
    try {
      await api.deleteRate(r.id);
      setArmedDeleteId(null);
      reload();
      showToast(`Rate deleted — ${r.category}${r.model_key ? ` · ${r.model_key}` : ""}. Its usage now shows as unpriced.`);
    } catch (e: any) {
      setSaveError(e?.message || "Delete failed — rates unchanged.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <h3 style={h3}>Rate card <span style={{ textTransform: "none", letterSpacing: 0, color: "var(--text-faint)" }}>· {isSuper ? "editable" : "read-only"}</span></h3>
      {error ? (
        <p style={{ color: "var(--danger-500)", fontSize: 13.5 }}>{error}</p>
      ) : loading || !rates ? (
        <p style={{ color: "var(--text-muted)", fontSize: 13.5 }}>Loading rate card…</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={th}>Category</th><th style={th}>Model</th><th style={th}>Unit</th>
            <th style={thR}>Unit cost (USD)</th>{isSuper && <th style={thR}></th>}
          </tr></thead>
          <tbody>
            {rates.map((r) => {
              const editing = editingId === r.id;
              return (
                <tr key={r.id}>
                  <td style={td}><span style={{ fontSize: 13.5, color: "var(--ink-900)" }}>{r.category}</span></td>
                  <td style={{ ...td, fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--text-muted)" }}>{r.model_key || "—"}</td>
                  <td style={{ ...td, fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--text-muted)" }}>{r.unit}</td>
                  <td style={tdR}>
                    {editing ? (
                      <input value={draft} onChange={(e) => setDraft(e.target.value)} inputMode="decimal" style={{ ...dateInput, width: 110, textAlign: "right" }} aria-label={`Unit cost for ${r.category}${r.model_key ? ` ${r.model_key}` : ""}`} />
                    ) : (
                      <span style={{ display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                        <span style={{ color: "var(--ink-900)" }}>${r.unit_cost_usd}</span>
                        {/* Flag rates still on their seeded placeholder value (#813) —
                            they are guesses, not confirmed vendor prices. */}
                        {(r.note || "").toLowerCase().includes("placeholder") && (
                          <Pill tone="neutral">placeholder</Pill>
                        )}
                      </span>
                    )}
                  </td>
                  {isSuper && (
                    <td style={{ ...tdR, whiteSpace: "nowrap" }}>
                      {editing ? (
                        <span style={{ display: "inline-flex", gap: 6 }}>
                          <Button variant="primary" size="sm" onClick={() => save(r)} disabled={saving || !draftValid}>{saving ? "Saving…" : "Save"}</Button>
                          <Button variant="ghost" size="sm" onClick={cancel} disabled={saving}>Cancel</Button>
                        </span>
                      ) : armedDeleteId === r.id ? (
                        <span style={{ display: "inline-flex", gap: 6 }}>
                          <Button variant="ghost" size="sm" style={{ color: "var(--danger-500)" }} onClick={() => remove(r)} disabled={busyId === r.id}>{busyId === r.id ? "Deleting…" : "Confirm delete"}</Button>
                          <Button variant="ghost" size="sm" onClick={() => setArmedDeleteId(null)} disabled={busyId === r.id}>Cancel</Button>
                        </span>
                      ) : (
                        <span style={{ display: "inline-flex", gap: 6 }}>
                          <Button variant="ghost" size="sm" onClick={() => startEdit(r)}>Edit</Button>
                          <Button variant="ghost" size="sm" style={{ color: "var(--danger-500)" }} onClick={() => { setArmedDeleteId(r.id); setSaveError(null); }}>Delete</Button>
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {saveError && <p style={{ color: "var(--danger-500)", fontSize: 13, marginTop: 8 }}>{saveError}</p>}
      {isSuper && rates && <AddRateForm onCreated={reload} showToast={showToast} />}
      {!isSuper && rates && rates.length > 0 && (
        <p style={{ marginTop: 8, fontSize: 12.5, color: "var(--text-faint)" }}>Only a superadmin can edit rates. Editing recomputes the costs above.</p>
      )}
    </>
  );
}

// A newly-deployed model needs a rate row before its usage can be priced (#805);
// without this an ops-only person had to wait for a code migration.
const RATE_CATEGORIES = ["llm_prompt", "llm_completion", "llm_cached", "telephony", "stt", "fixed_infra"] as const;

function unitForCategory(category: string): string {
  if (category.startsWith("llm_")) return "per_1k_tokens";
  if (category === "fixed_infra") return "per_month";
  return "per_minute";
}

function AddRateForm({ onCreated, showToast }: { onCreated: () => void; showToast: (m: string) => void }) {
  const [category, setCategory] = useState<string>("llm_prompt");
  const [modelKey, setModelKey] = useState("");
  const [cost, setCost] = useState("");
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isLlm = category.startsWith("llm_");
  const unit = unitForCategory(category);
  const costValid = cost.trim() !== "" && Number.isFinite(Number(cost.trim())) && Number(cost.trim()) >= 0;
  const modelValid = isLlm ? modelKey.trim() !== "" : true;
  const canCreate = costValid && modelValid && !creating;

  const create = async () => {
    setCreating(true);
    setError(null);
    try {
      await api.createRate({
        category,
        model_key: isLlm ? modelKey.trim() : "",
        unit_cost_usd: cost.trim(),
        note: note.trim() || undefined,
      });
      setModelKey(""); setCost(""); setNote("");
      onCreated(); // reload rate card + recompute costs
      showToast(`Rate added — ${category}${isLlm ? ` · ${modelKey.trim()}` : ""}.`);
    } catch (e: any) {
      setError(e?.message || "Create failed — rate not added.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={{ marginTop: 14, padding: "12px 14px", background: "var(--surface-sunken)", border: "1px solid var(--border-hairline)", borderRadius: 10 }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>Add a rate</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select value={category} onChange={(e) => setCategory(e.target.value)} style={dateInput} aria-label="Category">
          {RATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input value={modelKey} onChange={(e) => setModelKey(e.target.value)} placeholder={isLlm ? "model_key (e.g. gpt-5.6-terra)" : "n/a for telephony/stt"} disabled={!isLlm} style={{ ...dateInput, width: 230, opacity: isLlm ? 1 : 0.5 }} aria-label="Model key" />
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>{unit}</span>
        <input value={cost} onChange={(e) => setCost(e.target.value)} inputMode="decimal" placeholder="unit cost USD" style={{ ...dateInput, width: 130 }} aria-label="Unit cost USD" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="note (optional)" style={{ ...dateInput, width: 200 }} aria-label="Note" />
        <Button variant="primary" size="sm" onClick={create} disabled={!canCreate}>{creating ? "Adding…" : "Add rate"}</Button>
      </div>
      {error && <p style={{ color: "var(--danger-500)", fontSize: 13, marginTop: 8 }}>{error}</p>}
    </div>
  );
}

// --- page --------------------------------------------------------------------

export default function Cost({ effectiveRole, showToast }: PageProps) {
  const isSuper = effectiveRole === "SUPERADMIN";

  const [period, setPeriod] = useState<Period>({ kind: "preset", preset: "last_30d" });
  const [summary, setSummary] = useState<api.CostSummary | null>(null);
  const [byOrg, setByOrg] = useState<api.CostByOrg | null>(null);
  const [summaryErr, setSummaryErr] = useState<string | null>(null);
  const [byOrgErr, setByOrgErr] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(true);

  const [rates, setRates] = useState<api.RateRow[] | null>(null);
  const [ratesErr, setRatesErr] = useState<string | null>(null);
  const [ratesLoading, setRatesLoading] = useState(true);

  const params = useMemo(() => periodParams(period), [period]);
  // Monotonic guard: a slower earlier load (old period) must never overwrite a
  // newer one. Covers both the period-change effect and the rate-save re-fetch.
  const reqSeq = useRef(0);

  const loadReport = useCallback(async () => {
    const seq = ++reqSeq.current;
    setReportLoading(true);
    setSummaryErr(null);
    setByOrgErr(null);
    // Independent loads — one failing must not blank the other section.
    const [s, b] = await Promise.allSettled([api.getCostSummary(params), api.getCostByOrg(params)]);
    if (seq !== reqSeq.current) return; // superseded by a newer load
    if (s.status === "fulfilled") setSummary(s.value);
    else { setSummary(null); setSummaryErr(s.reason?.message || "Failed to load cost summary."); }
    if (b.status === "fulfilled") setByOrg(b.value);
    else { setByOrg(null); setByOrgErr(b.reason?.message || "Failed to load per-org costs."); }
    setReportLoading(false);
  }, [params]);

  const loadRates = useCallback(async () => {
    setRatesLoading(true);
    setRatesErr(null);
    try {
      setRates((await api.getRateCard()).rates);
    } catch (e: any) {
      setRates(null);
      setRatesErr(e?.message || "Failed to load rate card.");
    } finally {
      setRatesLoading(false);
    }
  }, []);

  useEffect(() => { loadReport(); }, [loadReport]);
  useEffect(() => { loadRates(); }, [loadRates]);

  const exportCsv = () => {
    if (!summary || !byOrg) return;
    const csv = buildCostCsv(summary, byOrg);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `wingscript-cost_${summary.period_start}_${summary.period_end}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: "24px 32px 48px", maxWidth: 1160 }}>
      <PeriodBar
        period={period}
        onApply={setPeriod}
        onExport={exportCsv}
        canExport={!!summary && !!byOrg}
        generatedAt={summary?.generated_at ?? null}
      />
      <SummarySection summary={summary} loading={reportLoading} error={summaryErr} />
      <OrgTable byOrg={byOrg} loading={reportLoading} error={byOrgErr} />
      <RateCardPanel
        rates={rates}
        loading={ratesLoading}
        error={ratesErr}
        isSuper={isSuper}
        showToast={showToast}
        reload={() => { loadRates(); loadReport(); }}
        onSaved={(updated) => {
          setRates((prev) => (prev ? prev.map((r) => (r.id === updated.id ? updated : r)) : prev));
          loadReport(); // recompute displayed costs against the new rate
        }}
      />
    </div>
  );
}
